/**
 * Exports Base44 Product records to a CSV in eBay File Exchange
 * "Fixed Price" listing format (UK marketplace). No eBay API needed —
 * the admin downloads this file and uploads it via eBay File Exchange.
 *
 * Uses the per-product eBay fields (ebay_category_id, ebay_shipping_service, …)
 * set in the product form's "eBay Listing" section.
 *
 * Products without an ebay_category_id are skipped (eBay requires a category).
 * The Description column carries the product's HTML description verbatim.
 */

// eBay File Exchange "Add" action header — SiteID baked into the heading
const ACTION_HEADER = 'Action(SiteID=United Kingdom)';

// [File Exchange column, mapper(product)]
const COLUMNS = [
  [ACTION_HEADER,                  () => 'Add'],
  ['Category',                     p => p.ebay_category_id || ''],
  ['StoreCategory',                () => ''],
  ['Title',                        p => (p.name || '').slice(0, 80)],
  ['Subtitle',                     () => ''],
  ['Relationship',                 () => ''],
  ['RelationshipDetails',          () => ''],
  ['ConditionID',                  () => '1000'], // 1000 = New
  ['PicURL',                       p => [p.image_url, p.image_url_2, p.image_url_3, p.image_url_4].filter(Boolean).join(',')],
  ['Quantity',                     p => {
    const q = p.stock_quantity;
    if (typeof q === 'number' && q > 0) return String(q);
    return p.in_stock === false ? '0' : '5'; // untracked → default to 5 available
  }],
  ['Currency',                     () => 'GBP'],
  ['StartPrice',                   p => (typeof p.price === 'number' ? p.price.toFixed(2) : '')],
  ['Format',                       () => 'FixedPrice'],
  ['Duration',                     p => {
    const map = { GTC: 'GTC', Days_7: '7', Days_10: '10', Days_30: '30' };
    return map[p.ebay_listing_duration] || 'GTC';
  }],
  ['BuyItNowPrice',                () => ''],
  ['ImmediatePayRequired',         p => (p.ebay_paypal_email ? '1' : '0')],
  ['Location',                     p => p.ebay_item_location || ''],
  ['PostalCode',                   p => p.ebay_item_postcode || ''],
  ['Country',                      p => p.ebay_item_country || 'GB'],
  ['PayPalAccepted',               p => (p.ebay_paypal_email ? '1' : '0')],
  ['PayPalEmailAddress',           p => p.ebay_paypal_email || ''],
  ['PaymentInstructions',          () => ''],
  ['ShippingService-1:Option',     p => p.ebay_shipping_service || ''],
  ['ShippingService-1:Cost',       p => (typeof p.ebay_shipping_cost === 'number' ? p.ebay_shipping_cost.toFixed(2) : '0.00')],
  ['ShippingService-1:Priority',   () => '1'],
  ['ShippingType',                 () => 'Flat'],
  ['DispatchTimeMax',              p => (typeof p.ebay_dispatch_time_max === 'number' ? String(p.ebay_dispatch_time_max) : '2')],
  ['CustomLabel',                  p => p.sku || ''], // seller SKU / custom label
  ['ProductIDValue',               p => p.gtin || ''],
  ['ProductIDType',                p => (p.gtin ? 'EAN' : '')],
  ['ReturnsAcceptedOption',        p => (p.ebay_returns_accepted === false ? 'ReturnsNotAccepted' : 'ReturnsAccepted')],
  ['ReturnsRefundOption',          () => 'MoneyBack'],
  ['ReturnsReturnsWithinOption',   p => p.ebay_returns_within || 'Days_30'],
  ['ReturnsShippingCostPaidByOption', p => p.ebay_return_shipping_paid_by || 'Buyer'],
  ['ItemID',                       p => p.ebay_listing_id || ''],
  ['Description',                  p => p.description || ''],
];

function escapeCsv(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/**
 * Builds and downloads an eBay File Exchange CSV for the given products.
 * @returns {{ exported: number, skipped: number }} so callers can show feedback.
 */
export function exportProductsToEbayCsv(products) {
  const eligible = (products || []).filter(p => p.ebay_category_id);
  const skipped = (products || []).length - eligible.length;

  const headers = COLUMNS.map(([heading]) => escapeCsv(heading)).join(',');
  const rows = eligible.map(p =>
    COLUMNS.map(([, mapper]) => escapeCsv(mapper(p))).join(',')
  );

  const csv = [headers, ...rows].join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ebay_file_exchange_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  return { exported: eligible.length, skipped };
}